import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import PhoneInput, { validatePhone, formatPhone } from "../../components/PhoneInput";

const GENDERS = ["", "Male", "Female", "Other"];

/* All helpers OUTSIDE parent to prevent focus loss */
function FormField({ label, value, onChange, placeholder, error, type }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type || "text"}
        className={`input ${error ? "border-red-400 focus:ring-red-400" : ""}`}
        placeholder={placeholder || ""}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function PatientCreate() {
  const navigate = useNavigate(); // FIX: use navigate instead of window.location.href
  const [form, setForm] = useState({ name: "", age: "", gender: "", phone: { countryCode: "+91", number: "" }, address: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) {
      e.name = "Name is required";
    } else if (!/^[a-zA-Z\s'.\-]+$/.test(form.name.trim())) {
      e.name = "Name should contain only letters and spaces";
    }
    if (!form.gender) e.gender = "Please select a gender";
    const phoneErr = validatePhone(form.phone, false);
    if (phoneErr) e.phone = phoneErr;
    if (form.age && (isNaN(form.age) || Number(form.age) <= 0 || Number(form.age) > 130)) {
      e.age = "Please enter a valid age (1–130)";
    }
    return e;
  };

  const setField = (key, value) => {
    setForm(p => ({ ...p, [key]: value }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      await api.post("/patients", { ...form, phone: formatPhone(form.phone) });
      navigate("/staff-portal/dashboard"); // FIX: was window.location.href which wiped sessionStorage
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || "Failed to create patient. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">New Walk-in Patient</h1>
        <p className="text-sm text-gray-500 mt-1">Register a new patient for today's OPD</p>
      </div>

      <form onSubmit={submit} className="card space-y-4">
        <FormField
          label="Full Name *"
          value={form.name}
          onChange={v => setField("name", v)}
          placeholder="Patient's full name"
          error={errors.name}
        />

        <FormField
          label="Age"
          value={form.age}
          onChange={v => setField("age", v.replace(/\D/g, ""))}
          placeholder="Age in years"
          error={errors.age}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
          <select
            className={`input ${errors.gender ? "border-red-400 focus:ring-red-400" : ""}`}
            value={form.gender}
            onChange={e => setField("gender", e.target.value)}
          >
            {GENDERS.map(g => <option key={g} value={g}>{g || "— Select gender —"}</option>)}
          </select>
          {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
        </div>

        <PhoneInput
          label="Phone Number"
          value={form.phone}
          onChange={v => setField("phone", v)}
          error={errors.phone}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            className="input"
            placeholder="Patient's address (optional)"
            rows={2}
            value={form.address}
            onChange={e => setField("address", e.target.value)}
          />
        </div>

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm">
            {errors.submit}
          </div>
        )}

        <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
          {loading ? "Creating…" : "Create Patient"}
        </button>
      </form>
    </div>
  );
}